import { readdir, readFile } from "node:fs/promises";
import { basename, extname, join } from "node:path";
import { parseFrontmatter } from "@vortex-os/core";
import type { ToolRule, ToolRuleFrontmatter, ToolRuleSeverity } from "./types.js";

/**
 * Directory-backed tool-rule catalog. One `.md` file per rule.
 *
 * The catalog reads all `.md` files in `dir` on each query (no caching).
 */
export class ToolRuleCatalog {
  constructor(readonly dir: string) {}

  /** Load every rule in the directory, sorted by id. */
  async list(): Promise<readonly ToolRule[]> {
    const files = await this.entries();
    const out: ToolRule[] = [];
    for (const file of files) {
      out.push(await this.loadFile(file));
    }
    return out.sort((a, b) => a.id.localeCompare(b.id));
  }

  /** Load a single rule by id. Returns `undefined` if not found. */
  async get(id: string): Promise<ToolRule | undefined> {
    const all = await this.list();
    return all.find((r) => r.id === id);
  }

  /** Return only rules whose `scope` matches exactly. */
  async filterByScope(scope: string): Promise<readonly ToolRule[]> {
    const all = await this.list();
    return all.filter((r) => r.scope === scope);
  }

  /** Return only rules whose `severity` matches. */
  async filterBySeverity(severity: ToolRuleSeverity): Promise<readonly ToolRule[]> {
    const all = await this.list();
    return all.filter((r) => r.severity === severity);
  }

  private async entries(): Promise<string[]> {
    try {
      const names = await readdir(this.dir);
      return names
        .filter((n) => n.endsWith(".md"))
        .map((n) => join(this.dir, n));
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") return [];
      throw e;
    }
  }

  private async loadFile(path: string): Promise<ToolRule> {
    const raw = await readFile(path, "utf8");
    const { frontmatter, body } = parseFrontmatter<ToolRuleFrontmatter>(raw);
    const idFromFile = basename(path, extname(path));
    return {
      id: frontmatter.id ?? idFromFile,
      title: frontmatter.title,
      scope: frontmatter.scope,
      severity: frontmatter.severity,
      condition: frontmatter.condition,
      action: frontmatter.action,
      body: body.trimStart(),
    };
  }
}
